/**
*   A wrapper for Flickity
*   https://flickity.metafizzy.co/
*
*   in the component that uses this mixin,
*   create an object as data-prop, called:
*   'SLIDE_SHOW_OPTIONS' with specific options.
*
*   mixin exposes function 'onSlideChange(newIndex)'
*
*
*/

/**
    options:
    SLIDE_SHOW_OPTIONS: {
        wrapAround: starts over when end is reached
        cellAlign: placement of active slide
        prevNextButtons: UI buttons
        pageDots: UI dots
        killAbove: slide show only active below [screen size]
        uninterrupted: disables verticle scroll when active
    }

 */

import { mapState } from 'vuex'

// vendor
import Flickity from 'flickity'

export default {
    name: 'slide-show',
    data() {
        return {
            INIT_OPTIONS: {
                wrapAround: true,
                prevNextButtons: false,
                pageDots: false,
                killAbove: 1024,
                uninterrupted: true
            },
            SLIDE_INSTANCES: [],
            elements: [],
        }
    },

    computed: {

        ...mapState({
            screenWidth: state => state.globals.screenWidth,
            currentScroll: state => state.globals.currentScroll,
        }),

        uninterrupted() {
            return this.INIT_OPTIONS.uninterrupted || this.SLIDE_SHOW_OPTIONS.uninterrupted || false
        },
        
        toBeOrNotToBe() {
            return this.screenWidth > (this.SLIDE_SHOW_OPTIONS && this.SLIDE_SHOW_OPTIONS.killAbove || this.INIT_OPTIONS.killAbove)
        }

    },

    watch: {

        screenWidth: {
            immediate: true,
            handler() {
                this.giveBirthOrKill(this.toBeOrNotToBe)
            }
        }

    },
    methods: {

        getElements(el) {
            const hasScrollAttribute = el.attributes.getNamedItem('slide-show');
            // run this func for all children
            [...el.children].forEach(this.getElements)
            
            if (!hasScrollAttribute) {
                return
            }
            // if we havn't returned yet, save the element
            this.elements.push(el)
        },

        // either remove all, or create a slide for each element
        giveBirthOrKill(killThemAll) {
            if (killThemAll) {
                if (!this.SLIDE_INSTANCES.length) {
                    return
                }

                this.SLIDE_INSTANCES.forEach(slideInstance => this.destroy(slideInstance))
            } else {
                this.elements.forEach(el => this.create(el))
            }
        },

        // init a new slide with all options
        create(el) {

            this.SLIDE_INSTANCES.push(new Flickity(el, {
                ...this.INIT_OPTIONS,
                ...this.SLIDE_SHOW_OPTIONS
            }))

            // hook up event listeners
            this.SLIDE_INSTANCES.forEach(slideInstance => {
                
                // change slide to the clicked slide
                slideInstance.on('staticClick', (event, pointer, cellElement, cellIndex) => {
                    this.changeToSlide(slideInstance, cellIndex)
                })

                // if present: run onSlideChange(index) in the shared context
                slideInstance.on('change', index => {
                    if (typeof this.onSlideChange != 'function') {
                        return
                    }
                    this.onSlideChange(index)
                })

                // disable verticle scroll
                slideInstance.on('dragStart', () => {
                    if (!this.uninterrupted) {
                        return
                    }
                    
                    document.ontouchmove = e => e.preventDefault()
                });
                
                slideInstance.on('dragEnd', () => {
                    document.ontouchmove = () => true
                });
            })

        },

        // kill and revert to normal
        destroy(slideInstance) {
            slideInstance.destroy()
        },

        // put selected index in focus
        changeToSlide(slideInstance, int) {
            slideInstance.selectCell(int)
        }
    },

    mounted() {
        this.$nextTick(() => {
            // find all elements we want
            this.getElements(this._vnode.elm)

            // instantiate them, if screen is big
            this.giveBirthOrKill(this.toBeOrNotToBe)
        })
    }
}
